import { useEffect, useMemo, useState } from "react";
import { FileText, ListPlus } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import type { PageIssue, ProposedFix } from "@/types/technicalAudit";
import {
  getWordCountTone,
  readParagraphDiffs,
} from "./thinContentParagraphs";

/**
 * Suggested paragraph edits for a thin page.
 *
 * Ported from LowReadabilityDiffCard's per-sentence review, one granularity up:
 * each row is an EXISTING paragraph and its expanded rewrite, accepted or
 * rejected individually and editable in place. It replaces the expansion-brief
 * view (headings / purpose / draft sections), which asked the operator to
 * approve copy that would be appended to the end of the page rather than shown
 * against what is already there.
 *
 * The accepted subset IS the payload — the backend's `apply_paragraph_diffs`
 * rewrites exactly the paragraphs it is handed and skips anything whose live
 * text has drifted since generation. So a deselected row is genuinely not
 * written, and a stale row is refused rather than overwriting the page.
 *
 * Note what is NOT offered here: paragraphs containing an inline link, bold or
 * image never reach this list. The backend excludes them because rewriting a
 * paragraph through an LLM is the documented way to lose the link inside it —
 * and those links include the internal links this same tool inserts.
 */

interface RowState {
  accepted: boolean;
  proposed: string;
}

export interface ThinContentParagraphEditsProps {
  issue: PageIssue;
  /** The parsed expansion packet, so the emitted payload keeps its other keys. */
  fix: Record<string, unknown> | null;
  onEdit?: (value: ProposedFix) => void;
}

function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function ThinContentParagraphEdits({
  issue,
  fix,
  onEdit,
}: ThinContentParagraphEditsProps) {
  const diffs = useMemo(() => readParagraphDiffs(fix), [fix]);

  // Re-seed when the underlying suggestion changes, mirroring the readability
  // card's draftKey so a regenerate does not leave the previous edits behind.
  const draftKey = `${issue.id}:${JSON.stringify(diffs.map((d) => d.block_index))}`;
  const [draft, setDraft] = useState<{ key: string; rows: RowState[] } | null>(
    null,
  );
  const rows: RowState[] =
    draft?.key === draftKey
      ? draft.rows
      : diffs.map((d) => ({ accepted: true, proposed: d.proposed }));

  const update = (index: number, patch: Partial<RowState>) => {
    setDraft({
      key: draftKey,
      rows: rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    });
  };

  const acceptedCount = rows.filter((r) => r.accepted).length;

  // Running total across the ACCEPTED rows, counted off the live edited text so
  // it tracks both ticking and typing. Per-row counts answer "is this one
  // bigger"; only the total answers the question the operator actually has —
  // "is this enough, and what will the page end up at".
  const addedWords = diffs.reduce((sum, diff, i) => {
    const row = rows[i];
    if (!row?.accepted) return sum;
    const before = diff.words_before ?? wordCount(diff.original);
    return sum + (wordCount(row.proposed) - before);
  }, 0);
  const pageWords = Number(fix?.word_count);
  const threshold = Number(fix?.threshold);
  const hasPageTotals = Number.isFinite(pageWords) && pageWords > 0;
  const projected = hasPageTotals ? pageWords + addedWords : null;
  // Tone and bar read off the PROJECTED figure, so the colour answers
  // "where would this land" rather than "where is it now" — the static card
  // above already answers the latter.
  const shown = projected ?? 0;
  const tone = getWordCountTone(shown, Number.isFinite(threshold) ? threshold : shown);
  const percentage = Number.isFinite(threshold) && threshold > 0
    ? Math.min((shown / threshold) * 100, 100)
    : 0;

  // Serialized accepted set — keeps the effect from refiring every render.
  const acceptedKey = JSON.stringify(
    rows.map((r, i) => (r.accepted ? [diffs[i]?.block_index, r.proposed] : null)),
  );

  useEffect(() => {
    if (!fix) return;
    const accepted = diffs
      .map((d, i) => ({ ...d, proposed: rows[i]?.proposed ?? d.proposed }))
      .filter((_, i) => rows[i]?.accepted);
    onEdit?.({ ...fix, paragraph_diffs: accepted } as ProposedFix);
    // acceptedKey is the stable serialization of what would be written.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, acceptedKey, onEdit]);

  if (diffs.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="thin-content-paragraph-edits">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <ListPlus className="h-4 w-4 text-teal-dark" />
          Suggested paragraph edits
        </div>
        <span
          className="text-xs text-muted-foreground"
          data-testid="thin-content-paragraphs-selected"
        >
          {acceptedCount} of {diffs.length} selected
        </span>
      </div>

      {/* The same readout as the static "Word count" card above, but LIVE: it
          projects where the page lands with the currently selected edits. The
          previous version was a sentence of numbers ("+16 words … 268 → 284 …
          16 short") that made the reader assemble the picture themselves.
          Reusing the familiar card means the number in the big slot always
          means the same thing on this screen.

          Sticky so it stays visible while scrolling a long list of rows — the
          total is only useful while you are ticking rows further down. */}
      <Card
        className="sticky top-0 z-10 border-border shadow-sm"
        data-testid="thin-content-paragraphs-total"
      >
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-warm-bg text-teal-dark">
              <FileText className="h-4 w-4" />
            </div>
            Projected word count
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          <div className="flex items-end justify-between gap-3">
            <div>
              <div
                className={`text-2xl font-bold tabular-nums ${tone.text}`}
                data-testid="thin-content-projected-words"
              >
                {projected ?? addedWords}
              </div>
              <div className="text-xs text-muted-foreground">
                {projected !== null ? "words after selected edits" : "words added"}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-foreground tabular-nums">
                {Number.isFinite(threshold) ? threshold : "—"}
              </div>
              <div className="text-xs text-muted-foreground">review threshold</div>
            </div>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-warm-bg">
            <div
              className={`h-full rounded-full transition-all ${tone.bar}`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {addedWords >= 0 ? "+" : "−"}
            {Math.abs(addedWords)} word{Math.abs(addedWords) === 1 ? "" : "s"} from{" "}
            {acceptedCount} selected edit{acceptedCount === 1 ? "" : "s"}
            {projected !== null && hasPageTotals ? ` · was ${pageWords}` : ""}
          </p>
        </CardContent>
      </Card>

      {diffs.map((diff, index) => {
        const row = rows[index] ?? { accepted: true, proposed: diff.proposed };
        const before = diff.words_before ?? wordCount(diff.original);
        const after = wordCount(row.proposed);
        return (
          <Card
            key={`${draftKey}:${diff.block_index}`}
            className={`border-border shadow-sm ${row.accepted ? "" : "opacity-60"}`}
          >
            <CardContent className="space-y-3 p-4">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={row.accepted}
                  onChange={(e) => update(index, { accepted: e.target.checked })}
                  aria-label="Include this paragraph edit"
                  data-testid={`thin-content-paragraph-accept-${index}`}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-coral)]"
                />
                <span className="flex flex-wrap items-center gap-2 text-xs font-medium text-foreground">
                  Paragraph {diff.block_index + 1}
                  <span className="text-muted-foreground">
                    {before} → {after} words
                  </span>
                </span>
              </label>

              {/* Which of these two survives flips with the checkbox, so the
                  emphasis has to flip with it. Unticked: the current text is
                  what stays, so it reads as live copy while the replacement is
                  struck through as discarded. Ticked: the replacement is the
                  page's future, so it stays prominent and the current text
                  recedes. Emphasising the current text permanently would point
                  at the paragraph being REPLACED whenever a row is ticked —
                  the opposite of the intent. */}
              <div>
                <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Currently on the page
                  {!row.accepted && (
                    <span className="ml-2 normal-case tracking-normal text-teal-dark">
                      · stays as-is
                    </span>
                  )}
                </label>
                <p
                  data-testid={`thin-content-paragraph-original-${index}`}
                  className={`mt-1 whitespace-pre-wrap rounded-md border p-2 text-sm ${
                    row.accepted
                      ? "border-border bg-muted/50 text-muted-foreground"
                      : "border-teal/30 bg-card font-medium text-foreground"
                  }`}
                >
                  {diff.original}
                </p>
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Suggested replacement
                  {!row.accepted && (
                    <span className="ml-2 normal-case tracking-normal text-muted-foreground">
                      · not applying
                    </span>
                  )}
                </label>
                <Textarea
                  value={row.proposed}
                  disabled={!row.accepted}
                  onChange={(e) => update(index, { proposed: e.target.value })}
                  data-testid={`thin-content-paragraph-proposed-${index}`}
                  className={`mt-1 min-h-[120px] resize-y border-teal/20 bg-card text-sm leading-relaxed ${
                    row.accepted ? "" : "text-muted-foreground line-through"
                  }`}
                />
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* The acceptance test is "does this add real information?", not "is it
          longer?". Google is explicit that thin content is about value, not word
          count, and a padded paragraph makes the page worse. */}
      <p className="text-xs text-muted-foreground">
        Accept an edit only if it adds real information. Extra words alone do not
        fix thin content — padding makes the page weaker, not stronger.
      </p>
    </div>
  );
}
